import type { TAttendanceDashboardState } from '@src/models/interfaces/attendanceDashboard.interface';

const BASE_URL = 'work-journey';

export const PATHS = {
  BASE_URL,
  CREATE_WORK_JOURNEY: `${BASE_URL}/create`,
  CREATE_FIXED_JOURNEY: `${BASE_URL}/create/fixed`,
  CREATE_PART_TIME_JOURNEY: `${BASE_URL}/create/part-time`,
  CREATE_SHIFT_JOURNEY: `${BASE_URL}/create/shift`,
  CREATE_CYCLE_JOURNEY: `${BASE_URL}/create/cycle`,
  CREATE_BI_WEEKLY_JOURNEY: `${BASE_URL}/create/bi-weekly`,
  EDIT_WORK_JOURNEY: `${BASE_URL}/edit/:id`,
  WORK_JOURNEY_DETAILS: `${BASE_URL}/details/:id`,
  REAL_TIME: `${BASE_URL}/real-time`,
  WORK_JOURNEY_MANAGEMENT: `${BASE_URL}/management`,
  /**
   * Users×days month matrix for a single journey.
   * month is 1-based.
   */
  WORK_JOURNEY_MONTH_MATRIX: `${BASE_URL}/management/matrix/:workJourneyConfigId/:year/:month`,
  /** Nested drawer under management — cover position due to scheduled absences. */
  WORK_JOURNEY_COVER_ABSENCE: 'cover-absence/:workJourneyConfigId/:date',
  /** Nested drawer — select replacement users for a cover-absence flow (step 1). */
  WORK_JOURNEY_COVER_ABSENCE_REPLACE:
    'cover-absence/:workJourneyConfigId/:date/replace/:absentUserId',
  /** Nested drawer under management — cover position due to missing members. */
  WORK_JOURNEY_COVER_SHORTAGE: 'cover-shortage/:workJourneyConfigId/:date',
  /** Nested drawer under management — resolve rejected shift-change requests. */
  WORK_JOURNEY_RESOLVE_REJECTED_CHANGE: 'resolve-rejected-change/:workJourneyConfigId/:date',
};

/**
 * Relative path to the users×days month matrix for a journey.
 * @param {{ workJourneyConfigId: string, year: number, month: number }} params month is 1-based
 */
export const getRouteToWorkJourneyMonthMatrix = ({ workJourneyConfigId, year, month }) =>
  `${PATHS.WORK_JOURNEY_MANAGEMENT}/matrix/${workJourneyConfigId}/${year}/${month}`;

/**
 * Relative path (no org prefix) to open the cover-absence drawer.
 * @param {{ workJourneyConfigId: string, date: string }} params ISO date YYYY-MM-DD
 */
export const getRouteToCoverAbsenceDrawer = ({ workJourneyConfigId, date }) =>
  `${PATHS.WORK_JOURNEY_MANAGEMENT}/cover-absence/${workJourneyConfigId}/${date}`;

/**
 * Relative path to open the replacement-user picker under cover-absence.
 * @param {{ workJourneyConfigId: string, date: string, absentUserId: string }} params
 */
export const getRouteToCoverAbsenceReplaceDrawer = ({ workJourneyConfigId, date, absentUserId }) =>
  `${PATHS.WORK_JOURNEY_MANAGEMENT}/cover-absence/${workJourneyConfigId}/${date}/replace/${absentUserId}`;

/**
 * Relative path (no org prefix) to open the cover-shortage drawer.
 * @param {{ workJourneyConfigId: string, date: string }} params ISO date YYYY-MM-DD
 */
export const getRouteToCoverShortageDrawer = ({ workJourneyConfigId, date }) =>
  `${PATHS.WORK_JOURNEY_MANAGEMENT}/cover-shortage/${workJourneyConfigId}/${date}`;

/**
 * Relative path (no org prefix) to open the rejected-change resolve drawer.
 * @param {{ workJourneyConfigId: string, date: string }} params ISO date YYYY-MM-DD
 */
export const getRouteToResolveRejectedChangeDrawer = ({ workJourneyConfigId, date }) =>
  `${PATHS.WORK_JOURNEY_MANAGEMENT}/resolve-rejected-change/${workJourneyConfigId}/${date}`;

export const WORK_JOURNEY_LIST_PAGE_SIZE = 10;

export const WORK_JOURNEY_LIST_VIEW_STORAGE_KEY = 'workJourneyList';

export const WORK_JOURNEY_LIST_FILTERS_STORAGE_KEY = 'workJourneyListFilters';

export const WORK_JOURNEY_LIST_SORT_STORAGE_KEY = 'workJourneyListTableSort';

export const WORK_JOURNEY_LIST_SEARCH_IDS_SIZE = 100;

export const WORK_JOURNEY_USER_SEARCH_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'idCard',
] as const;

export const WORK_JOURNEY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  UPDATED: 'updated',
} as const;

export type WorkJourneyStatus = (typeof WORK_JOURNEY_STATUS)[keyof typeof WORK_JOURNEY_STATUS];

export const WORK_JOURNEY_PLACE_TYPE = {
  PRESENTIAL: 'presential',
  REMOTE: 'remote',
  WITHOUT_LOCATION: 'without_location',
} as const;

export type WorkJourneyPlaceType =
  (typeof WORK_JOURNEY_PLACE_TYPE)[keyof typeof WORK_JOURNEY_PLACE_TYPE];

export const JOURNEY_ASSIGNED_USERS_TYPE = {
  SUPERVISOR: 'supervisor',
  MEMBER: 'member',
};

export const TOTEM_NAME_MAX_LENGTH = 60;

export const TOTEM_EXPIRATION_HOURS_OPTIONS = [12, 24, 48];

export const TOTEM_NO_EXPIRATION_VALUE = null;

export type JourneyAssignedUsersType =
  (typeof JOURNEY_ASSIGNED_USERS_TYPE)[keyof typeof JOURNEY_ASSIGNED_USERS_TYPE];

export const USER_ATTENDANCE_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_TOLERANCE: 'IN_TOLERANCE',
  IN_PROGRESS: 'IN_PROGRESS',
  ABSENT: 'ABSENT',
} as const;

export const JOURNEY_ATTENDANCE_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_TOLERANCE: 'IN_TOLERANCE',
  IN_PROGRESS: 'IN_PROGRESS',
  BELOW_MINIMUM: 'BELOW_MINIMUM',
  /** Scheduled end has passed and staffing minimum is met (or not required). */
  FINISHED: 'FINISHED',
} as const;

/**
 * Per-user state shown in the Real-Time "Por usuario" tab and used by that
 * tab's Estado filter:
 * - marked within tolerance = ATTENDED
 * - marked after tolerance OR unmarked in the late window = LATE
 * - unmarked past the absence threshold (or leave) = ABSENT
 */
export const USER_ROW_ATTENDANCE_STATE = {
  NOT_STARTED: 'NOT_STARTED',
  ATTENDED: 'ATTENDED',
  LATE: 'LATE',
  ABSENT: 'ABSENT',
} as const;

/**
 * Aggregated status filters available only in Work Journey Real-Time.
 * - UNMARKED: NOT_STARTED + unmarked LATE (matches "Sin marcar" KPI)
 * - PRESENT: marked ATTENDED + marked LATE (matches "Presentes" KPI)
 */
export const REALTIME_AGGREGATE_STATUS = {
  UNMARKED: 'UNMARKED',
  PRESENT: 'PRESENT',
} as const;

export type UserAttendanceStatusCode =
  (typeof USER_ATTENDANCE_STATUS)[keyof typeof USER_ATTENDANCE_STATUS];

export type JourneyAttendanceStatusCode =
  (typeof JOURNEY_ATTENDANCE_STATUS)[keyof typeof JOURNEY_ATTENDANCE_STATUS];

export type UserRowAttendanceStateCode =
  (typeof USER_ROW_ATTENDANCE_STATE)[keyof typeof USER_ROW_ATTENDANCE_STATE];

export type RealtimeAggregateStatusCode =
  (typeof REALTIME_AGGREGATE_STATUS)[keyof typeof REALTIME_AGGREGATE_STATUS];

/** Status codes exposed by the Real-Time Estado filter (tab-scoped). */
export type RealtimeAttendanceStatusCode =
  | UserRowAttendanceStateCode
  | JourneyAttendanceStatusCode
  | RealtimeAggregateStatusCode;

export const WORK_JOURNEY_REAL_TIME_TAB = {
  BY_USER: 'byUser',
  BY_JOURNEY: 'byJourney',
} as const;

/** Attendance event types for `newAttendanceEvent` (mark attendance drawer). */
export const ATTENDANCE_EVENT_TYPE = {
  SHIFT_START: 'SHIFT_START',
  SHIFT_END: 'SHIFT_END',
  BREAK_START: 'BREAK_START',
  BREAK_END: 'BREAK_END',
} as const;

/** Actor roles accepted by `newAttendanceEvent`. */
export const ATTENDANCE_EVENT_ACTOR_ROLE = {
  EMPLOYEE: 'EMPLOYEE',
  SUPERVISOR: 'SUPERVISOR',
  SYSTEM: 'SYSTEM',
} as const;

export type WorkJourneyRealTimeTab =
  (typeof WORK_JOURNEY_REAL_TIME_TAB)[keyof typeof WORK_JOURNEY_REAL_TIME_TAB];

/** Attendance-dashboard conflict states used by management mappers and filters. */
export const ATTENDANCE_DASHBOARD_STATES = {
  UNDERSTAFFED_DUE_TO_ABSENCES: 'understaffed_due_to_absences' as TAttendanceDashboardState,
  INSUFFICIENT_ASSIGNED_MEMBERS: 'insufficient_assigned_members' as TAttendanceDashboardState,
  REJECTED_SHIFT_CHANGE: 'rejected_shift_change' as TAttendanceDashboardState,
  USERS_UNAVAILABLE: 'users_unavailable' as TAttendanceDashboardState,
};

/**
 * Estado filter options for the active Real-Time tab.
 * Built from tab-specific status constants — do not add literals here.
 */
export const getRealtimeStatusFilterCodesForTab = (
  tab: WorkJourneyRealTimeTab,
): RealtimeAttendanceStatusCode[] =>
  tab === WORK_JOURNEY_REAL_TIME_TAB.BY_USER
    ? [
        ...Object.values(USER_ROW_ATTENDANCE_STATE),
        REALTIME_AGGREGATE_STATUS.UNMARKED,
        REALTIME_AGGREGATE_STATUS.PRESENT,
      ]
    : [...Object.values(JOURNEY_ATTENDANCE_STATUS)];
